import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { formatNumber, getCurrencySymbolFromCache } from "../../../utils";
import { formatAdminAreaChain } from "../../../utils/adminArea";
import { getLocalisedName } from "../../../utils/i18n";
import { Carousel } from "./Carousel";
import { DepositDetailsModal } from "./DepositDetailsModal";
import { Deposit } from "./deposit-dtos";
import { StockStatusBadges } from "../components/StockStatusBadges";
import { canMakeOffer, formatDepositDate, offersClosedNoticeKey } from "../utils";

interface DepositCardProps {
  deposit: Deposit;
  onMakeOffer?: (deposit: Deposit) => void;
  onClickAggregatorDetails?: (id: number) => void;
  detailsPath?: string;
}

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col items-center text-center">
    <span className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
      {label}
    </span>
    <span className="font-semibold text-2xl text-foreground">{value}</span>
  </div>
);

export const DepositCard = ({
  deposit,
  onMakeOffer,
  onClickAggregatorDetails,
  detailsPath,
}: DepositCardProps) => {
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (!deposit) {
    return null;
  }

  const unit = deposit.unit_of_measure?.code;
  const currency = getCurrencySymbolFromCache();
  const commodity = getLocalisedName(deposit.farmer_produce?.commodity || deposit.commodity);
  const grade = deposit.quality_grade?.name || deposit.quality;
  const address = formatAdminAreaChain(deposit.aggregator_admin_area);

  const offerNumber =
    (!!deposit.direct_offers && deposit.direct_offers.length) + (deposit.market_order_id ? 1 : 0);

  const listedAt = formatDepositDate(deposit.backend_creation_time, {
    today: t("farmersMarket.depositCard.relativeDate.today"),
    yesterday: t("farmersMarket.depositCard.relativeDate.yesterday"),
  });

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
        <Carousel className="size-24 shrink-0" imageUrls={deposit.picture_urls || []} />

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
              {t("farmersMarket.depositCard.deposit")} #{deposit.id}
            </span>
            <span className="text-muted-foreground text-xs">
              {t("farmersMarket.depositCard.listed", { date: listedAt })}
            </span>
            {deposit.stock_status ? (
              <StockStatusBadges status={deposit.stock_status} />
            ) : (
              <Badge variant={offerNumber ? "warning" : "secondary"}>
                {offerNumber
                  ? `${offerNumber} ${t("farmersMarket.details.status.active")}`
                  : t("farmersMarket.details.status.noOffersYet")}
              </Badge>
            )}
          </div>

          <p className="font-semibold text-foreground text-lg">
            {grade ? `${commodity} · ${grade}` : commodity}
          </p>

          <p className="flex min-w-0 text-muted-foreground text-sm">
            {onClickAggregatorDetails && deposit.aggregator?.id ? (
              <WFPButton
                type="button"
                variant="link"
                className="h-auto shrink-0 p-0 font-normal text-muted-foreground text-sm"
                onClick={() => onClickAggregatorDetails(deposit.aggregator.id)}
              >
                {deposit.aggregator.name}
              </WFPButton>
            ) : (
              <span className="shrink-0">{deposit.aggregator?.name}</span>
            )}
            {address && (
              <span className="truncate" title={address}>
                &nbsp;· {address}
              </span>
            )}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-start gap-x-8 gap-y-3">
          <Metric
            label={t("farmersMarket.depositCard.available")}
            value={
              unit ? `${formatNumber(deposit.quantity)} ${unit}` : formatNumber(deposit.quantity)
            }
          />
          <Metric
            label={t("farmersMarket.depositCard.unitPrice")}
            value={`${currency} ${formatNumber(deposit.price)}`}
          />
          <Metric
            label={t("farmersMarket.depositCard.totalValue")}
            value={`${currency} ${formatNumber(Number(deposit.quantity) * Number(deposit.price))}`}
          />

          <div className="flex w-48 flex-col gap-2 self-center">
            {onMakeOffer &&
              (canMakeOffer(deposit) ? (
                <WFPButton className="w-full" onClick={() => onMakeOffer(deposit)}>
                  {t("farmersMarket.depositCard.offerToBuy")}
                </WFPButton>
              ) : (
                <span className="text-center text-muted-foreground text-sm">
                  {t(offersClosedNoticeKey(deposit))}
                </span>
              ))}

            <WFPButton variant="secondary" className="w-full" onClick={() => setDetailsOpen(true)}>
              {t("farmersMarket.depositCard.viewStockDetails")}
            </WFPButton>
          </div>
        </div>
      </div>

      <DepositDetailsModal
        deposit={deposit}
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        onMakeOffer={
          onMakeOffer &&
          (selected => {
            setDetailsOpen(false);
            onMakeOffer(selected);
          })
        }
        onClickAggregatorDetails={onClickAggregatorDetails}
        detailsPath={detailsPath}
      />
    </Card>
  );
};
